import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

export type AnnouncementTag = 'INFO' | 'IMPORTANT' | 'WARNING';

export interface Announcement {
  id: number;
  title: string;
  content: string;
  tag: AnnouncementTag;
  isPinned: boolean;
  isActive: boolean;
  videoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementRequest {
  title: string;
  content: string;
  tag: AnnouncementTag;
  isPinned: boolean;
  isActive?: boolean;
  videoUrl?: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const announcementsApi = {
  getAnnouncements: async (): Promise<Announcement[]> => {
    const res = await axios.get(`${API_BASE_URL}/announcements`, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  },

  // Admin
  getAllAnnouncements: async (): Promise<Announcement[]> => {
    const res = await axios.get(`${API_BASE_URL}/admin/announcements`, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  },

  create: async (request: AnnouncementRequest): Promise<Announcement> => {
    const res = await axios.post(`${API_BASE_URL}/admin/announcements`, request, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  },

  update: async (id: number, request: AnnouncementRequest): Promise<Announcement> => {
    const res = await axios.put(`${API_BASE_URL}/admin/announcements/${id}`, request, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/admin/announcements/${id}`, {
      headers: getAuthHeaders(),
    });
  },

  uploadVideo: async (file: File, onProgress?: (pct: number) => void): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API_BASE_URL}/admin/announcements/upload-video`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const res = await axios.get(`${API_BASE_URL}/announcements/unread-count`, {
      headers: getAuthHeaders(),
    });
    return res.data.data;
  },

  markSeen: async (): Promise<void> => {
    await axios.post(`${API_BASE_URL}/announcements/mark-seen`, {}, {
      headers: getAuthHeaders(),
    });
  },
};

export default announcementsApi;
